import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiCheckCircle, FiXCircle, FiLoader, FiRefreshCw } from 'react-icons/fi'

export default function VerifyLogin({ onLogin }) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const [status, setStatus] = useState('verifying') // verifying, success, error
  const [message, setMessage] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [verificationAttempted, setVerificationAttempted] = useState(false)

  useEffect(() => {
    // Check if user is already logged in (from successful verification)
    const existingToken = localStorage.getItem('token')
    if (existingToken) {
      // User is already logged in, redirect to dashboard
      navigate('/dashboard')
      return
    }

    // Prevent multiple verification attempts
    if (verificationAttempted) {
      return
    }

    const verifyLogin = async () => {
      if (!token) {
        setStatus('error')
        setMessage('Invalid verification link. No token provided.')
        return
      }

      setVerificationAttempted(true)

      try {
        const response = await axios.post('/auth/verify-login', { token })
        const { user, token: jwtToken } = response.data
        
        setStatus('success')
        setMessage('Confirmation successful! Logging you in...')
        
        // Auto-login the user
        setTimeout(() => {
          onLogin(user, jwtToken)
          navigate('/dashboard')
        }, 2000)
        
      } catch (error) {
        console.error('Verification error:', error)
        setStatus('error')
        setMessage(error.response?.data?.message || 'Verification failed. Please try logging in again.')
      }
    }

    if (token && !verificationAttempted) {
      verifyLogin()
    } else if (!token) {
      setStatus('error')
      setMessage('Invalid verification link. Please request a new verification email.')
    }
  }, [token, onLogin, navigate, verificationAttempted])

  const handleResendVerification = async () => {
    if (!email.trim()) {
      toast.error('Please enter your email address')
      return
    }

    setResendLoading(true)
    try {
      const response = await axios.post('/auth/login', { email: email.trim(), password: 'dummy' })
      if (response.data.requiresVerification) {
        toast.success('New verification email sent. Please check your email.')
        setEmail('')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend verification')
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 sm:py-12 px-4">
      <div className="max-w-xs sm:max-w-md w-full space-y-6 sm:space-y-8">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            Login Verification
          </h2>
          <p className="mt-2 text-xs sm:text-sm text-gray-600">
            Verifying your login request...
          </p>
        </div>

        <div className="bg-white py-6 sm:py-8 px-3 sm:px-6 shadow rounded-lg">
          <div className="text-center">
            {status === 'verifying' && (
              <div className="flex flex-col items-center">
                <FiLoader className="h-10 sm:h-12 w-10 sm:w-12 text-blue-500 animate-spin" />
                <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-700">Verifying your login...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center">
                <FiCheckCircle className="h-10 sm:h-12 w-10 sm:w-12 text-green-500" />
                <p className="mt-3 sm:mt-4 text-sm sm:text-base text-green-700">{message}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="flex flex-col items-center">
                <FiXCircle className="h-10 sm:h-12 w-10 sm:w-12 text-red-500" />
                <p className="mt-3 sm:mt-4 text-sm sm:text-base text-red-700 text-center">{message}</p>
                
                <div className="mt-4 sm:mt-6 w-full">
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                    Request a new verification link by entering your email:
                  </p>
                  <div className="space-y-2 sm:space-y-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={resendLoading}
                    />
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <button
                        onClick={handleResendVerification}
                        disabled={resendLoading || !email.trim()}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
                      >
                        {resendLoading ? (
                          <>
                            <FiLoader className="animate-spin h-4 w-4" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <FiRefreshCw className="h-4 w-4" />
                            <span>Resend</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => navigate('/login')}
                        className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm sm:text-base transition"
                      >
                        Back
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}